import { Size, type CertificateCard as CertificateCardFacts } from '@bms2/shared';
import { Pill } from '../controls';
import { RowMenu, type RowMenuEntry } from './RowMenu';
import { TaxExemptBadge } from './TaxExemptBadge';
import styles from './CertificateCard.module.css';

/**
 * 🔒 One Tax Exempt certificate as a small fact card (piece 13, the Site page's certificates tile; T3082, decision
 * 7440). The header is the gold badge and the state written out with its code; a label-and-value grid sits beneath
 * it; the foot carries the one ⋯ menu, holding Edit, View Certificate and End, and no End button stands on the face.
 * An ended, expired or awaiting-review card reads muted with its pill and wears no badge. The words and the rule for
 * which card wears the badge come from the one shared function, so the phone draws the same card.
 */
export function CertificateCard({ card, menu, menuLabel, id }: { card: CertificateCardFacts; menu: readonly RowMenuEntry[]; menuLabel: string; id?: string | null }) {
  return (
    <div className={`${styles.card} ${card.muted ? styles.muted : ''}`} data-certificate-card={id ?? 'row'} data-muted={card.muted ? 'true' : undefined}>
      <div className={styles.head}>
        {card.badge ? (
          <span className={styles.badgeSlot}>
            <TaxExemptBadge badge={card.badge} />
          </span>
        ) : null}
        <span className={styles.title} data-certificate-title="true">{card.title}</span>
        {card.pill ? (
          <span className={styles.pill} data-tile-pill={card.pill.group}>
            <Pill text={card.pill.text} foreground={`var(--status-${card.pill.group}-text)`} background={`var(--status-${card.pill.group}-bg)`} size={Size.Small} />
          </span>
        ) : null}
      </div>
      <dl className={styles.facts}>
        {card.facts.map((fact) => (
          <div key={fact.label} className={`${styles.fact} ${fact.wide ? styles.wide : ''}`} data-certificate-fact={fact.label}>
            <dt className={styles.label}>{fact.label}</dt>
            <dd className={styles.value}>{fact.value}</dd>
          </div>
        ))}
      </dl>
      {menu.length > 0 ? (
        <div className={styles.foot}>
          <RowMenu label={menuLabel} entries={menu} />
        </div>
      ) : null}
    </div>
  );
}
